import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { ComboBox, EmailInput, Text, ToastContext } from '@reveni-io/reveni-ui'
import { Divider } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import PortalsInUseList from '@/configuration/components/PortalsInUseList/PortalsInUseList'
import SettingsSection from '@/configuration/components/SettingsSection/SettingsSection'
import { settingsTypes } from '@/configuration/constants'
import { sectionTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getConfigurationMode, getEntityIdByConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import useSettingsType from '@/configuration/pages/ConfigurationPage/hooks/useSettingsType/useSettingsType'

import BillingEmail from './BillingEmail/BillingEmail'
import CommunicationsGeneralConfigurationSkeleton from './CommunicationsGeneralConfigurationSkeleton/CommunicationsGeneralConfigurationSkeleton'
import useCommunicationsConfiguration from './hooks/useCommunicationsConfiguration'
import ToggleCommunications from './ToggleCommunications/ToggleCommunications'
import { replyToEmailOptions, replyToEmailTypes, transactionalCommunicationsOptions } from './constants'
import {
  filterReplyToOptionsBySubscriptionPlan,
  getCcEmails,
  getPayload,
  getRecipientEmail,
  isNotificationEnabled,
} from './helpers'
import * as Styles from './styles'

const CommunicationsGeneralConfiguration = ({ isUnifiedDashboard }) => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)

  const {
    settingsType,
    setSettingsType,
    hasChanges: settingsTypeHasChanges,
    revertSettingsType,
    onSaveSettingsType,
    loadingStoreSettingsType,
  } = useSettingsType({
    returnPortalId: info?.selectedReturnPortal?.id,
    section: sectionTypes.communications,
    isUnifiedDashboard,
    setToastInfo,
  })

  const configurationMode = getConfigurationMode({ isUnifiedDashboard, settingsType })
  const entityId = getEntityIdByConfigurationMode({
    configurationMode,
    merchantId: info?.id,
    storeId: info?.selectedStore?.id,
    returnPortalId: info?.selectedReturnPortal?.id,
  })

  const {
    gettingCommunicationsPreferences,
    refetchCommunicationsPreferences,
    replyToEmail,
    setReplyToEmail,
    replyToEmailType,
    setReplyToEmailType,
    notificationTypes,
    editCommunicationsPreferences,
    applyingCommunicationsPreferences,
    hasChanges,
    revertChanges,
    handleChangeRecipientEmail,
    handleChangeTransactionalCommunications,
    handleChangeCcEmails,
    language,
    setLanguage,
    billingEmail,
    onChangeBillingEmail,
    billingCcEmails,
    onChangeBillingCcEmails,
    enabledPortalsWithGlobal,
  } = useCommunicationsConfiguration({
    storeId: info.selectedStore.id,
    storeInfo: info.selectedStore,
    configurationMode,
    merchantId: info.id,
    returnPortalId: info?.selectedReturnPortal?.id,
  })
  const { hasPermission } = useUserPermissions()

  const onSave = async () => {
    const payload = getPayload({
      replyToEmail,
      replyToEmailType,
      notificationTypes,
      defaultEmail: info.email,
      language,
      billingEmail,
      billingCcEmails,
    })

    try {
      if (!isUnifiedDashboard && settingsTypeHasChanges) {
        await onSaveSettingsType()
      }

      await editCommunicationsPreferences({ ...entityId, preferences: payload })
      await refetchCommunicationsPreferences(entityId)

      setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const handleChangeSettingsType = type => {
    setSettingsType(type)
  }

  const handleResetChanges = () => {
    revertSettingsType()
    revertChanges()
  }

  const isDisabledByGlobalSettings = !isUnifiedDashboard && settingsType === settingsTypes.global

  if (gettingCommunicationsPreferences || applyingCommunicationsPreferences || loadingStoreSettingsType) {
    return <CommunicationsGeneralConfigurationSkeleton />
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert hasPermission={hasPermission(userPermissions.editCommunications)} />}
      {isUnifiedDashboard && (
        <Styles.PortalsInUseContainer>
          <PortalsInUseList portals={info.returnPortals} portalsInUse={enabledPortalsWithGlobal} />
        </Styles.PortalsInUseContainer>
      )}
      {!isUnifiedDashboard && info.allows_unified_dashboard && (
        <SettingsSection type={settingsType} onChange={handleChangeSettingsType} />
      )}
      <Styles.TransactionalCommunicationsSection>
        <Text type="titleM">{t('Dirección Reply-to')}</Text>
        <Text type="bodyMRegular">
          {t(
            'Define la dirección de Reply-To de las comunicaciones a tus clientes, a la que podrán escribir en el caso de tener dudas, incidencias, etc:'
          )}
        </Text>
        <ComboBox
          menuItems={filterReplyToOptionsBySubscriptionPlan(replyToEmailOptions(t), info?.subscription_plan)}
          value={replyToEmailType}
          handleChange={e => setReplyToEmailType(e.target.value)}
          fullWidth
          label={t('Usar dirección de Reply-to')}
          disabled={isDisabledByGlobalSettings}
        />
        {replyToEmailType === replyToEmailTypes.custom_email && (
          <EmailInput
            label={t('Email de respuesta para clientes')}
            placeholder="contact@acme.com"
            value={replyToEmail}
            onChange={e => setReplyToEmail(e.target.value)}
            fullWidth
            disabled={isDisabledByGlobalSettings}
          />
        )}

        <Divider />
        <BillingEmail
          email={billingEmail}
          onChangeEmail={onChangeBillingEmail}
          allowsDirectDebit={info?.billing_data?.direct_debit_scheme}
          ccEmails={billingCcEmails}
          onChangeCcEmails={onChangeBillingCcEmails}
          disabled={isDisabledByGlobalSettings}
        />
      </Styles.TransactionalCommunicationsSection>
      <Divider />
      <Styles.TransactionalCommunicationsSection>
        <Text type="titleM">{t('Comunicaciones transaccionales')}</Text>
        <Text type="bodyMRegular">
          {t(
            'Configura las comunicaciones que recibirás sobre la actividad de tu Portal de devoluciones y el idioma en el que quieres recibirlas.'
          )}
        </Text>
        {language && (
          <ComboBox
            label={t('Idioma comunicaciones')}
            menuItems={[
              { label: 'Español - ES', value: 'es' },
              { label: 'English - EN', value: 'en' },
            ]}
            value={language}
            handleChange={e => setLanguage(e.target.value)}
            disabled={isDisabledByGlobalSettings}
          />
        )}
        <Styles.TransactionalCommunicationsSwitchContainer>
          {transactionalCommunicationsOptions.map((option, index) => (
            <div key={index}>
              {!option.hideForSubscriptionPlans.includes(info?.subscription_plan) && (
                <ToggleCommunications
                  key={option.identifier}
                  label={t(option.label)}
                  description={t(option.description)}
                  isActive={isNotificationEnabled(option.identifier, notificationTypes)}
                  defaultRecipient={info.email}
                  onChangeRecipient={value => handleChangeRecipientEmail(option.identifier, value)}
                  recipient={getRecipientEmail(option.identifier, notificationTypes, info.email)}
                  onChangeIsActive={e => handleChangeTransactionalCommunications(option.identifier, e.target.checked)}
                  ccEmails={getCcEmails(option.identifier, notificationTypes)}
                  onChangeCcEmails={value => handleChangeCcEmails(option.identifier, value)}
                  disabled={isDisabledByGlobalSettings}
                />
              )}
            </div>
          ))}
        </Styles.TransactionalCommunicationsSwitchContainer>
      </Styles.TransactionalCommunicationsSection>
      <SaveChangesButtons
        hasChanges={hasPermission(userPermissions.editCommunications) && (hasChanges || settingsTypeHasChanges)}
        onSave={onSave}
        onRevertChanges={handleResetChanges}
        loading={applyingCommunicationsPreferences || gettingCommunicationsPreferences}
        disabled={applyingCommunicationsPreferences || gettingCommunicationsPreferences}
      />
    </Styles.Container>
  )
}

export default CommunicationsGeneralConfiguration
